import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import { useEffect, useRef } from "react";
import { adminUploadFile } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Underline as U, Highlighter, Heading2, Heading3, Pilcrow,
  List, ListOrdered, Quote, AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo, Redo, ImagePlus,
} from "lucide-react";

const Btn = ({ on, active, title, children, disabled }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => { e.preventDefault(); on(); }}
    disabled={disabled}
    className={`flex h-8 w-8 items-center justify-center rounded-none border border-line text-ink-2 transition-colors hover:bg-surface-alt hover:text-ink disabled:opacity-40 ${active ? "bg-[rgba(199,80,0,0.12)] text-saffron" : "bg-parchment"}`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder, deva }) {
  const imgInputRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, underline: false }),
      Underline,
      Highlight.configure({ multicolor: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Image.configure({ inline: false, HTMLAttributes: { class: "rte-image" } }),
      Placeholder.configure({ placeholder: placeholder || "" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class: `prose-editor min-h-[120px] w-full max-w-none px-3 py-2 text-sm text-ink focus:outline-none ${deva ? "font-deva-body" : ""}`,
      },
    },
  });

  // Keep editor content in sync when the form loads async data.
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const onImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 12 * 1024 * 1024) { toast.error("Image too large (max 12 MB)."); return; }
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      const res = await adminUploadFile(file);
      const url = res.url?.startsWith("http") ? res.url : `${BACKEND_URL}${res.url}`;
      editor.chain().focus().setImage({ src: url }).run();
      toast("Image inserted");
    } catch (err) {
      toast.error("Image upload failed", { description: err.response?.data?.detail || err.message });
    }
  };

  return (
    <div className="border border-line bg-parchment">
      <div className="flex flex-wrap gap-1 border-b border-line bg-surface p-1.5">
        <Btn on={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold"><Bold className="h-4 w-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic"><Italic className="h-4 w-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline"><U className="h-4 w-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive("highlight")} title="Highlight"><Highlighter className="h-4 w-4" /></Btn>
        <span className="mx-0.5 w-px bg-line" />
        <Btn on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Large heading"><Heading2 className="h-4 w-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Small heading"><Heading3 className="h-4 w-4" /></Btn>
        <Btn on={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")} title="Paragraph"><Pilcrow className="h-4 w-4" /></Btn>
        <span className="mx-0.5 w-px bg-line" />
        <Btn on={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list"><List className="h-4 w-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list"><ListOrdered className="h-4 w-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote"><Quote className="h-4 w-4" /></Btn>
        <span className="mx-0.5 w-px bg-line" />
        <Btn on={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left"><AlignLeft className="h-4 w-4" /></Btn>
        <Btn on={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center"><AlignCenter className="h-4 w-4" /></Btn>
        <Btn on={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right"><AlignRight className="h-4 w-4" /></Btn>
        <Btn on={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justify"><AlignJustify className="h-4 w-4" /></Btn>
        <span className="mx-0.5 w-px bg-line" />
        <Btn on={() => editor.chain().focus().undo().run()} title="Undo" disabled={!editor.can().undo()}><Undo className="h-4 w-4" /></Btn>
        <Btn on={() => editor.chain().focus().redo().run()} title="Redo" disabled={!editor.can().redo()}><Redo className="h-4 w-4" /></Btn>
        <span className="mx-0.5 w-px bg-line" />
        <Btn on={() => imgInputRef.current?.click()} title="Insert image"><ImagePlus className="h-4 w-4" /></Btn>
      </div>
      <input ref={imgInputRef} type="file" accept="image/*" onChange={onImageFile} className="hidden" data-testid="rte-image-input" />
      <EditorContent editor={editor} />
    </div>
  );
}
